<template>
    <div class="row h-100 d-flex flex-column flex-nowrap">
        <!-- Credit card update modal -->
        <b-modal
            v-model="showUpdateCardDialog"
            size="lg"
            :title="paymentMethod ? 'Update Billing' : 'Add Billing'"
            modal-class="vue-need-bootstrap"
            lazy
            centered
            hide-footer
            no-close-on-backdrop
            class="mt-3"
        >
            <credit-card-input
                :stripe-public-key="stripePublicKey"
                :store="storeUrl"
                :fromPodBilling="true"
                :new-card="paymentMethod == null"
                @card-updated="onCardSubmit"
            />
        </b-modal>

        <!-- Credit card -->
        <div class="mt-3">
            <div class="card white-card shadow-sm">
                <div class="card-body">
                    <h4 class="card-title mb-3">POD Billing Details</h4>
                    <div :class="{ busy: loadingPaymentMethod }">
                        <div v-if="loadingPaymentMethod" class="text-center">
                            <b-spinner variant="primary" class="my-2" />
                        </div>
                        <div v-else class="row small-card-content" :class="{ 'has-card': paymentMethod }">
                            <div class="col-10 row">
                                <div
                                    v-if="!paymentMethod && !errorGettingPaymentMethod"
                                    class="col-10 align-items-center d-flex"
                                >
                                    Please enter your credit card details so we can pay the POD every time you sell
                                    their products.
                                </div>
                                <div v-if="errorGettingPaymentMethod" class="col-10 align-items-center d-flex">
                                    There was an error getting the credit card information.
                                </div>
                                <!-- Card information -->
                                <div class="col-6">
                                    <div v-if="paymentMethod" class="align-items-center d-flex">
                                        <!-- Card icon -->
                                        <card-icon class="d-inline" :card="paymentMethod.Brand" />

                                        <div>
                                            <h5 class="d-inline ml-2">Card ending in {{ paymentMethod.Last4 }}</h5>
                                            <small class="card-expiration card-expiration ml-1"
                                                >exp. {{ paymentMethod.ExpMonth.toString().padStart(2, '0') }}/{{
                                                    paymentMethod.ExpYear
                                                }}</small
                                            >
                                        </div>
                                    </div>
                                    <div v-if="customer && customer.Email" class="mt-3">
                                        Billing contact:
                                        <strong>{{ customer.Email }}</strong>
                                    </div>
                                </div>

                                <!-- Billing information -->
                                <div v-if="paymentMethod" class="col-6">
                                    <ul>
                                        <li v-if="paymentMethod.BillingDetails.Name">
                                            {{ paymentMethod.BillingDetails.Name }}
                                        </li>
                                        <li v-if="paymentMethod.BillingDetails.Address1">
                                            {{ paymentMethod.BillingDetails.Address1 }}
                                        </li>
                                        <li
                                            v-if="
                                                paymentMethod.BillingDetails.Country &&
                                                paymentMethod.BillingDetails.City
                                            "
                                        >
                                            {{ paymentMethod.BillingDetails.City }},
                                            {{ paymentMethod.BillingDetails.Country
                                            }}<span v-if="paymentMethod.BillingDetails.State"
                                                >, {{ paymentMethod.BillingDetails.State }}</span
                                            ><span v-if="paymentMethod.BillingDetails.PostalCode"
                                                >, {{ paymentMethod.BillingDetails.PostalCode }}</span
                                            >
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div
                                v-if="paymentMethod && !errorGettingPaymentMethod"
                                class="col d-flex align-items-end justify-content-end"
                            >
                                <button class="btn btn-primary" @click="editCreditCard()">Edit billing</button>
                            </div>
                            <div
                                v-if="!paymentMethod && !errorGettingPaymentMethod"
                                class="col d-flex align-items-end justify-content-end"
                            >
                                <button class="btn btn-primary" @click="editCreditCard()">Add Credit Card</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Invoices -->
        <div class="mt-3 invoice-card">
            <div class="card white-card shadow-sm h-100">
                <div v-if="loadingInvoices" class="card-body d-flex flex-column">
                    <h4 class="card-title mb-3">POD Billing History</h4>
                    <div v-if="loadingPaymentMethod" class="text-center">
                        <b-spinner variant="primary" class="my-2" />
                    </div>
                </div>
                <div v-if="!loadingInvoices" class="card-body d-flex flex-column">
                    <h4 class="card-title mb-3">POD Billing History</h4>
                    <div v-if="!paymentMethod && !errorGettingInvoices" class="bg-light text-dark p-3 rounded">
                        <font-awesome-icon :icon="['fa-solid', 'fa-circle-info']" class="my-auto mr-2" />
                        Your POD billing history will appear here once transactions are made. Get started by entering
                        your payment details to activate POD billing.
                    </div>
                    <div
                        v-if="paymentMethod && !errorGettingInvoices && invoiceItems && invoiceItems.length == 0"
                        class="bg-light text-dark p-3 rounded"
                    >
                        <font-awesome-icon :icon="['fa-solid', 'fa-circle-info']" class="my-auto mr-2" />
                        Your POD billing history will appear here once transactions are made.
                    </div>
                    <div v-if="errorGettingInvoices" class="bg-light text-dark p-3 rounded">
                        There was an error getting the invoices.
                    </div>
                    <b-table
                        v-if="paymentMethod && !errorGettingInvoices && invoiceItems && invoiceItems.length > 0"
                        class="invoice-table flex-grow-1"
                        sticky-header
                        hover
                        fixed
                        show-empty
                        :fields="invoiceTableFields"
                        :items="invoiceItems"
                        :busy="loadingInvoices"
                        empty-text="No invoices"
                    >
                        <!-- Empty table indicator -->
                        <template #empty="scope">
                            <div class="text-center mt-3">
                                <h3>{{ scope.emptyText }}</h3>
                            </div>
                        </template>

                        <!-- Loader -->
                        <template #table-busy>
                            <div class="align-items-center d-flex justify-content-center w-100 mt-3">
                                <b-spinner variant="primary" class="my-2" />
                            </div>
                        </template>

                        <!-- Date -->
                        <template #cell(date)="data">
                            <div>{{ data.value }}</div>
                        </template>

                        <!-- Description -->
                        <template #cell(description)>
                            <div>Customily Service</div>
                        </template>

                        <!-- Amount -->
                        <template #cell(amount)="data">
                            <div>{{ data.value }}</div>
                        </template>

                        <!-- Status -->
                        <template #cell(status)="data">
                            <div
                                class="text-capitalize"
                                :class="{ paid: data.value === 'paid', 'primary-text': data.value === 'paid' }"
                            >
                                {{ data.value }}
                            </div>
                        </template>

                        <!-- View invoice -->
                        <template #cell(invoice)="data">
                            <a :href="data.value" target="_blank" rel="noopener noreferrer">
                                <i class="fa fa-eye invoice-icon" />
                            </a>
                        </template>
                    </b-table>

                    <!-- Pagination buttons -->
                    <div
                        v-if="paymentMethod && !errorGettingInvoices && invoiceItems && invoiceItems.length > 0"
                        class="d-flex justify-content-center"
                    >
                        <div class="btn-group">
                            <button
                                class="btn btn-secondary btn-white shadow-none"
                                type="button"
                                :class="{ disabled: loadingInvoices || disablePrevBtn }"
                                @click="goPrevInvoicesPage()"
                            >
                                ‹
                            </button>
                            <button
                                class="btn btn-secondary btn-white shadow-none"
                                type="button"
                                :class="{ disabled: loadingInvoices || disableNextBtn }"
                                @click="goNextInvoicesPage()"
                            >
                                ›
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Ref, computed, onBeforeMount, ref, watch } from 'vue';
import dateformat from 'dateformat';
import { AxiosError } from 'axios';
import store from '@/store';
import { getCustomer, getDefaultPaymentMethod, getInvoices } from '@/api/billing';
import type { Customer, Invoice, PaymentMethod } from '@/api/typings/billing';
import CardIcon from './CardIcon/CardIcon.vue';
import CreditCardInput from './CreditCardInput/CreditCardInput.vue';
import { StoreType } from '@/typings/user';

const emit = defineEmits(['user-updated']);

const invoicesPerPage = 10;
const invoiceTableFields = [
    { key: 'date', label: 'Date' },
    { key: 'description', label: 'Description' },
    { key: 'amount', label: 'Amount', class: 'text-center' },
    { key: 'status', label: 'Status' },
    { key: 'invoice', label: 'Invoice', class: 'text-center' }
];

const storeUrl = ref('');
const customer: Ref<Customer | null> = ref(null);
const paymentMethod: Ref<PaymentMethod | null> = ref(null);
const invoices: Ref<Invoice[]> = ref([]);
const loadingPaymentMethod = ref(false);
const loadingInvoices = ref(false);
const errorGettingPaymentMethod = ref(false);
const errorGettingInvoices = ref(false);
const showUpdateCardDialog = ref(false);
const disablePrevBtn = ref(false);
const disableNextBtn = ref(false);

const user = computed(() => store.state.user);

const stripePublicKey = computed(() => store.state.stripePublicKey);

const invoiceItems = computed(() => {
    return invoices.value.map((i) => ({
        date: formatDateString(new Date(i.Created)),
        amount: `$${(i.Total / 100).toFixed(2)}`,
        status: i.Status,
        invoice: i.InvoicePdf
    }));
});

onBeforeMount(async () => {
    try {
        loadingPaymentMethod.value = true;
        loadingInvoices.value = true;
        //If the user has more than one shopify store, all of them will have the same customer, so the first one is taken
        let userStore = store.state.user?.Stores.find((s) => s.Type == StoreType.Shopify && s.HasCustomerId);
        if (!userStore) {
            userStore = store.state.user?.Stores.find((s) => s.Type == StoreType.Shopify);
        }
        storeUrl.value = userStore?.Url || '';

        if (!storeUrl.value) {
            return;
        }

        //Get payment method
        try {
            errorGettingPaymentMethod.value = false;
            customer.value = await getCustomer(storeUrl.value);
            if (!customer.value) {
                return;
            }
            paymentMethod.value = await getDefaultPaymentMethod(storeUrl.value);
        } catch (error) {
            const axiosError = error as AxiosError;
            if (!axiosError || (axiosError && axiosError.request?.status !== 404)) {
                window.rumReportError(axiosError);
                paymentMethod.value = null;
                errorGettingPaymentMethod.value = true;
            }
        }

        //Get invoices
        try {
            const data = await getInvoices(storeUrl.value, invoicesPerPage);
            invoices.value = data.Invoices;
            disableNextBtn.value = false;
            disablePrevBtn.value = true;
            if (!data.MoreAvailable) {
                disableNextBtn.value = true;
            }
        } catch (error) {
            const axiosError = error as AxiosError;
            if (!axiosError || (axiosError && axiosError.request?.status !== 404)) {
                window.rumReportError(axiosError);
                invoices.value = [];
                errorGettingInvoices.value = true;
            }
        }
    } catch (error) {
        window.rumReportError(error as Error);
    } finally {
        loadingPaymentMethod.value = false;
        loadingInvoices.value = false;
    }
});

const formatDateString = (date: Date | string): string => {
    return dateformat(date, 'mmm dd, yyyy');
};

const goPrevInvoicesPage = async () => {
    try {
        loadingInvoices.value = true;
        const data = await getInvoices(storeUrl.value, invoicesPerPage, undefined, invoices.value[0].Id);
        invoices.value = data.Invoices;

        disableNextBtn.value = disablePrevBtn.value = false;
        if (!data.MoreAvailable) {
            disablePrevBtn.value = true;
        }
    } finally {
        loadingInvoices.value = false;
    }
};

const goNextInvoicesPage = async () => {
    try {
        loadingInvoices.value = true;
        const data = await getInvoices(storeUrl.value, invoicesPerPage, invoices.value[invoices.value.length - 1].Id);
        invoices.value = data.Invoices;

        disableNextBtn.value = disablePrevBtn.value = false;
        if (!data.MoreAvailable) {
            disableNextBtn.value = true;
        }
    } finally {
        loadingInvoices.value = false;
    }
};

const editCreditCard = () => {
    showUpdateCardDialog.value = true;
};

const onCardSubmit = async () => {
    showUpdateCardDialog.value = false;
    try {
        loadingPaymentMethod.value = true;
        emit('user-updated');
        try {
            errorGettingPaymentMethod.value = false;
            paymentMethod.value = await getDefaultPaymentMethod(storeUrl.value);
        } catch (error) {
            const axiosError = error as AxiosError;
            if (!axiosError || (axiosError && axiosError.request?.status !== 404)) {
                window.rumReportError(axiosError);
                paymentMethod.value = null;
                errorGettingPaymentMethod.value = true;
            }
        }
        if (!customer.value) {
            //The customer is created when the user adds the credit card.
            customer.value = await getCustomer(storeUrl.value);
        }
    } finally {
        loadingPaymentMethod.value = false;
    }
};
</script>

<style lang="scss">
.invoice-table {
    // https://stackoverflow.com/questions/7060009/css-max-height-remaining-space
    max-height: initial !important;

    td {
        padding: 0.3rem !important;
        vertical-align: middle !important;
    }

    th {
        padding: 0.3rem !important;
    }

    tr th:first-child,
    tr td:first-child {
        padding-left: 0 !important;
    }
}
</style>

<style lang="scss" scoped>
li {
    list-style-type: none;
    padding-left: 0;
}

.invoice-card {
    min-height: 400px;
}

.small-card-content.has-card {
    min-height: 70px;
}

.paid {
    font-weight: bold;
}

.card-expiration {
    color: gray;
}

.invoice-icon {
    color: gray;
    font-size: 18px;
}
</style>
